/* eslint-disable */
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  Grid,
  Collapse,
} from '@mui/material';
import {
  Mic,
  Stop,
  Send,
  ArrowBack,
  Edit,
  RestartAlt,
  ExpandMore,
  Lightbulb,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import CVPreview from '../components/CVPreview';
import { generateCV } from '../services/cvService';

/*
 * Mobile‑first refactor
 * – Stacks grids vertically < md
 * – Shrinks paddings & font sizes on xs
 * – Collapsible preview on mobile to save space
 */

const SUPPORTED_AUDIO_TYPES = ['audio/mp4', 'audio/webm', 'audio/wav'];
const MAX_RECORDING_TIME = 600;

const LoadingSpinner = React.memo(({ message }) => (
  <Box sx={{ textAlign: 'center', py: 3 }}>
    <CircularProgress size={48} thickness={4} sx={{ mb: 1 }} />
    <Typography variant="body1">{message}</Typography>
  </Box>
));

const RecordingButton = React.memo(({ isRecording, recordingTime, onStart, onStop, isMobile }) => {
  const formatTime = useCallback((seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  }, []);

  const diameter = isMobile ? 80 : 120;
  const iconSize = isMobile ? 40 : 60;

  if (isRecording) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Box
          component={motion.div}
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ repeat: Infinity, duration: 1.4 }}
          sx={{
            width: diameter,
            height: diameter,
            borderRadius: '50%',
            bgcolor: 'error.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            mx: 'auto',
          }}
        >
          <Stop sx={{ color: '#fff', fontSize: iconSize }} />
        </Box>
        <Typography variant={isMobile ? 'body1' : 'h5'} sx={{ mb: 1 }}>
          Recording… {formatTime(recordingTime)}
        </Typography>
        <Button
          variant="contained"
          color="error"
          size={isMobile ? 'medium' : 'large'}
          startIcon={<Stop />}
          onClick={onStop}
        >
          Stop
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box
        component={motion.div}
        whileHover={{ scale: isMobile ? 1 : 1.05 }}
        sx={{
          width: diameter,
          height: diameter,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          mx: 'auto',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.3s ease'
        }}
        onClick={disabled ? undefined : onStart}
      >
        <Mic sx={{ color: '#fff', fontSize: iconSize }} />
      </Box>
      <Typography variant={isMobile ? 'body1' : 'h5'} sx={{ mb: 1 }}>
        Tap to Start Recording
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 300, mx: 'auto' }}>
        Speak clearly: personal info, experience, education, skills.
      </Typography>
      {!isMobile && (
        <Button variant="contained" onClick={onStart} startIcon={<Mic />} size="large">
          Start Voice Recording
        </Button>
      )}
    </Box>
  );
});

const AudioPlayer = React.memo(({ audioBlob, onClose }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    if (audioBlob) {
      const audioUrl = URL.createObjectURL(audioBlob);
      audioRef.current = new Audio(audioUrl);
      
      const audio = audioRef.current;
      audio.addEventListener('loadedmetadata', () => setDuration(audio.duration));
      audio.addEventListener('timeupdate', () => setCurrentTime(audio.currentTime));
      audio.addEventListener('ended', () => setIsPlaying(false));

      return () => {
        audio.pause();
        URL.revokeObjectURL(audioUrl);
      };
    }
  }, [audioBlob]);

  const togglePlayback = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  if (!audioBlob) return null;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={togglePlayback} color="primary">
            {isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <LinearProgress 
              variant="determinate" 
              value={duration ? (currentTime / duration) * 100 : 0}
              sx={{ mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              {formatTime(currentTime)} / {formatTime(duration)}
            </Typography>
          </Box>
          <VolumeUp color="action" />
          <Button size="small" onClick={onClose}>
            Remove
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
});

const TranscriptStats = React.memo(({ transcript }) => {
  const wordCount = transcript.trim().split(/\s+/).filter(word => word.length > 0).length;
  const charCount = transcript.length;
  const isMinimumMet = charCount >= TRANSCRIPT_MIN_LENGTH;

  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
      <Chip 
        icon={<Description />}
        label={`${charCount} characters`}
        color={isMinimumMet ? 'success' : 'warning'}
        size="small"
      />
      <Chip 
        icon={<Psychology />}
        label={`${wordCount} words`}
        color="primary"
        size="small"
      />
      {!isMinimumMet && (
        <Chip 
          icon={<Warning />}
          label={`Need ${TRANSCRIPT_MIN_LENGTH - charCount} more chars`}
          color="warning"
          size="small"
        />
      )}
    </Box>
  );
});

const VoicePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState('');
  const [cvMarkdown, setCvMarkdown] = useState('');
  const [loading, setLoading] = useState(false);
  const [formTemplate] = useState('modern');
  const [previewOpen, setPreviewOpen] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  const getAudioMimeType = useCallback(() => {
    for (const t of SUPPORTED_AUDIO_TYPES) if (MediaRecorder.isTypeSupported(t)) return t;
    return 'audio/webm';
  }, []);

  const startRecording = useCallback(async () => {
    try {
      setError('');
      audioChunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = getAudioMimeType();
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: mime });
      mediaRecorderRef.current.ondataavailable = (e) => e.data.size && audioChunksRef.current.push(e.data);
      mediaRecorderRef.current.onstop = processAudio;
      mediaRecorderRef.current.start();
      setIsRecording(true);
      timerRef.current = setInterval(() =>
        setRecordingTime((t) => {
          if (t >= MAX_RECORDING_TIME) stopRecording();
          return t + 1;
        }),
      1000);
    } catch (err) {
      setError(err.message || 'Microphone error');
    }
  }, [getAudioMimeType]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      clearInterval(timerRef.current);
      setIsRecording(false);
      mediaRecorderRef.current.stream.getTracks().forEach((trk) => trk.stop());
    }
  }, [isRecording, showSnackbar]);

  const processAudio = useCallback(async () => {
    setIsProcessing(true);
    try {
      const blob = new Blob(audioChunksRef.current, { type: getAudioMimeType() });
      const file = new File([blob], 'recording.webm', { type: blob.type });
      const formData = new FormData();
      formData.append('file', file);
      formData.append('model', 'whisper-1');
      const res = await fetch('https://api.openai.com/v1/audio/transcriptions', {
        method: 'POST',
        headers: { Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Transcription failed');
      const data = await res.json();
      setTranscript(data.text);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
      setProcessingProgress(0);
    }
  }, [getAudioMimeType]);

  const generate = useCallback(async () => {
    if (!transcript.trim()) return setError('Record or enter info first');
    setLoading(true);
    setError('');
    try {
      const data = await generateCV({ experience: transcript, template: formTemplate, fullTranscript: transcript });
      setCvMarkdown(data.cv);
      setPreviewOpen(true);
    } catch (e) {
      setError(e.message || 'Generation failed');
    } finally {
      setLoading(false);
    }
  }, [transcript, formTemplate]);

  const reset = () => {
    setTranscript('');
    setCvMarkdown('');
    setRecordingTime(0);
    setPreviewOpen(false);
  };

  useEffect(() => () => clearInterval(timerRef.current), []);

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 3, md: 4 } }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/')}>Back</Button>
        <Typography variant={isMobile ? 'h5' : 'h4'} ml={2} fontWeight={700}>
          Voice CV Builder
        </Typography>
      </Box>

      {/* Tips */}
      <Accordion defaultExpanded={!isMobile} sx={{ mb: { xs: 3, md: 4 } }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center">
            <Lightbulb sx={{ mr: 1, color: 'warning.main' }} />
            <Typography variant="subtitle1">Tips for Best Results</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" component="div" sx={{ mb: 1 }}>
            Structure your speech:
          </Typography>
          <ul style={{ marginTop: 0 }}>
            <li>Full name, location, contact details</li>
            <li>Professional summary</li>
            <li>Work experience (company, dates, responsibilities)</li>
            <li>Education</li>
            <li>Technical & soft skills</li>
          </ul>
          <Typography variant="body2">Speak clearly and at a moderate pace.</Typography>
        </AccordionDetails>
      </Accordion>

      <Grid container spacing={isMobile ? 2 : 4} direction={isMobile ? 'column' : 'row'}>
        {/* Recording */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, md: 3 }, height: '100%' }}>
            <Typography variant={isMobile ? 'h6' : 'h5'} gutterBottom>
              Record Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {isProcessing ? (
              <LoadingSpinner message="Transcribing…" />
            ) : (
              <RecordingButton
                isRecording={isRecording}
                recordingTime={recordingTime}
                onStart={startRecording}
                onStop={stopRecording}
                isMobile={isMobile}
              />
            )}
          </Paper>
        </Grid>

        {/* Transcript */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant={isMobile ? 'h6' : 'h5'}>Transcript</Typography>
              <Box>
                <Tooltip title="Reset">
                  <IconButton onClick={reset} disabled={!transcript} size="small">
                    <RestartAlt fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton size="small">
                    <Edit fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <TextField
              multiline
              rows={isMobile ? 8 : 12}
              fullWidth
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              placeholder="Transcript will appear here…"
              sx={{ flexGrow: 1, mb: 2 }}
            />
            <Button
              variant="contained"
              startIcon={<Send />}
              disabled={!transcript.trim() || loading}
              onClick={generate}
              size={isMobile ? 'medium' : 'large'}
            >
              Generate CV
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Preview */}
      <Collapse in={previewOpen || loading} timeout="auto" unmountOnExit>
        <Paper sx={{ p: { xs: 2, md: 4 }, mt: { xs: 3, md: 4 } }}>
          <Typography variant={isMobile ? 'h6' : 'h5'} gutterBottom>
            CV Preview
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <CVPreview
            cvMarkdown={cvMarkdown}
            loading={loading}
            template={formTemplate}
            error={error}
            onRetry={generate}
            isPremiumUser={false}
          />
        </Paper>
      </Collapse>
    </Container>
  );
};

export default React.memo(VoicePage);
